<template>
  <transition name="baidu-lead-notice">
    <div
      v-if="visible && activeNotice"
      class="baidu-lead-notice"
      :class="{ 'baidu-lead-notice--blink': templateStyle.blinkEnabled }"
      :style="noticeStyle"
    >
      <div class="baidu-lead-notice__title">
        <i class="el-icon-bell"></i>
        <span>{{ activeNotice.title || '百度营销推广' }}</span>
      </div>
      <div class="baidu-lead-notice__content" :style="contentStyle">{{ activeNotice.content }}</div>
      <div class="baidu-lead-notice__meta" v-if="displayPhoneCandidate">
        手机号：{{ displayPhoneCandidate }}
      </div>
      <div class="baidu-lead-notice__actions">
        <el-button size="mini" type="primary" icon="el-icon-position" @click="goProcess">去处理</el-button>
      </div>
    </div>
  </transition>
</template>

<script>
import api from '@/services/api';
import { getToken } from '@/utils/sessionAuth';
import { buildManageLoginEntryUrl } from '@/utils/manageEntryRedirect.mjs';

const DEFAULT_PREFERENCE = {
  alertMode: 'BOTH',
  messageTypes: ['BAIDU_PUSH', 'URGENT_ALERT']
};
const DEFAULT_TEMPLATE_STYLE = {
  backgroundColor: 'white',
  fontColor: 'red',
  fontSize: '16px',
  blinkEnabled: true,
  voiceEnabled: true
};
const LAST_NOTIFICATION_EVENT_ID_KEY = 'baiduLeadNotifierLastEventId';

function safeColor(value, fallback) {
  const text = value == null ? '' : String(value).trim();
  if (!text) return fallback;
  if (/^[#(),.%\w\s-]+$/.test(text)) return text;
  return fallback;
}

function safeFontSize(value) {
  const text = value == null ? '' : String(value).trim();
  return /^\d{1,2}px$/.test(text) ? text : '16px';
}

export default {
  name: 'BaiduLeadNotifier',
  data() {
    return {
      eventSource: null,
      preference: { ...DEFAULT_PREFERENCE },
      activeNotice: null,
      visible: false,
      titleTimer: null,
      originalTitle: '',
      lastNotificationEventId: localStorage.getItem(LAST_NOTIFICATION_EVENT_ID_KEY) || '',
      reconnectTimer: null,
      reconnectDelay: 3000
    };
  },
  computed: {
    templateStyle() {
      const tpl = this.activeNotice && this.activeNotice.template ? this.activeNotice.template : {};
      return {
        backgroundColor: safeColor(tpl.backgroundColor, DEFAULT_TEMPLATE_STYLE.backgroundColor),
        fontColor: safeColor(tpl.fontColor, DEFAULT_TEMPLATE_STYLE.fontColor),
        fontSize: safeFontSize(tpl.fontSize),
        blinkEnabled: tpl.blinkEnabled !== false,
        voiceEnabled: tpl.voiceEnabled !== false
      };
    },
    noticeStyle() {
      return {
        backgroundColor: this.templateStyle.backgroundColor
      };
    },
    contentStyle() {
      return {
        color: this.templateStyle.fontColor,
        fontSize: this.templateStyle.fontSize
      };
    },
    displayPhoneCandidate() {
      const displayPhone = this.activeNotice && this.activeNotice.displayPhoneCandidate
        ? String(this.activeNotice.displayPhoneCandidate).trim()
        : '';
      if (displayPhone) return displayPhone;
      const rawPhone = this.activeNotice && this.activeNotice.phoneCandidate
        ? String(this.activeNotice.phoneCandidate).trim()
        : '';
      return this.maskPhoneMiddleFour(rawPhone);
    }
  },
  mounted() {
    this.originalTitle = document.title;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.teardown);
    this.loadPreference();
    this.connectStream();
    this.registerWebPush();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.teardown);
    this.teardown();
  },
  methods: {
    async loadPreference() {
      try {
        const res = await api.post('/system/message-template/notification-preference/query', {});
        const data = res.data && res.data.success ? res.data.data : null;
        if (data) {
          this.preference = {
            alertMode: data.alertMode || DEFAULT_PREFERENCE.alertMode,
            messageTypes: Array.isArray(data.messageTypes) && data.messageTypes.length
              ? data.messageTypes
              : DEFAULT_PREFERENCE.messageTypes.slice()
          };
        }
      } catch (e) {
        this.preference = {
          alertMode: DEFAULT_PREFERENCE.alertMode,
          messageTypes: DEFAULT_PREFERENCE.messageTypes.slice()
        };
      }
    },
    async registerWebPush() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') return;
      try {
        const keyRes = await api.post('/notifications/web-push/public-key', {});
        const config = keyRes.data && keyRes.data.success ? keyRes.data.data : null;
        if (!config || !config.enabled || !config.publicKey) return;
        let permission = Notification.permission;
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') return;

        const registration = await navigator.serviceWorker.register('/baidu-push-sw.js');
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(config.publicKey)
          });
        }
        await api.post('/notifications/web-push/subscribe', {
          subscription: subscription.toJSON ? subscription.toJSON() : subscription
        });
      } catch (e) {
        // Push API 依赖 HTTPS、浏览器权限和厂商 Push 服务；失败时保留 SSE/DOM 通道。
      }
    },
    urlBase64ToUint8Array(base64String) {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    },
    connectStream() {
      const token = getToken();
      if (!token || typeof EventSource === 'undefined') return;
      this.closeStream();
      const query = [`token=${encodeURIComponent(token)}`];
      if (this.lastNotificationEventId) {
        query.push(`lastEventId=${encodeURIComponent(this.lastNotificationEventId)}`);
      }
      const url = `/api/notifications/stream?${query.join('&')}`;
      this.eventSource = new EventSource(url);
      this.eventSource.addEventListener('ready', () => {
        this.reconnectDelay = 3000;
      });
      this.eventSource.addEventListener('message', (event) => {
        this.handleMessage(event);
      });
      this.eventSource.onerror = () => {
        this.closeStream();
        this.scheduleReconnect();
      };
    },
    scheduleReconnect() {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => {
        this.connectStream();
        this.reconnectDelay = Math.min(this.reconnectDelay + 2000, 15000);
      }, this.reconnectDelay);
    },
    closeStream() {
      if (this.eventSource) {
        this.eventSource.close();
      }
      this.eventSource = null;
    },
    teardown() {
      this.closeStream();
      clearTimeout(this.reconnectTimer);
      this.stopTitleBlink();
      this.stopSpeech();
    },
    handleMessage(event) {
      if (event && event.lastEventId) {
        this.lastNotificationEventId = event.lastEventId;
        localStorage.setItem(LAST_NOTIFICATION_EVENT_ID_KEY, event.lastEventId);
      }
      let payload = null;
      try {
        payload = JSON.parse(event.data || '{}');
      } catch (e) {
        payload = null;
      }
      if (payload && (payload.eventId || payload.id)) {
        this.lastNotificationEventId = payload.eventId || payload.id;
        localStorage.setItem(LAST_NOTIFICATION_EVENT_ID_KEY, this.lastNotificationEventId);
      }
      if (!payload || !this.shouldReceive(payload)) return;
      this.dispatchNotice(payload);
    },
    shouldReceive(payload) {
      const type = payload.type || '';
      const list = Array.isArray(this.preference.messageTypes) ? this.preference.messageTypes : [];
      return list.length === 0 || list.indexOf(type) !== -1;
    },
    maskPhoneMiddleFour(value) {
      const text = String(value || '').trim();
      const digits = text.replace(/\D/g, '');
      if (!/^1[3-9]\d{9}$/.test(digits)) return text;
      return `${digits.slice(0, 3)}****${digits.slice(7)}`;
    },
    dispatchNotice(payload) {
      const mode = this.preference.alertMode || 'BOTH';
      const isVisible = document.visibilityState === 'visible';
      if (mode === 'SYSTEM') {
        this.showSystemNotification(payload, true);
      } else if (mode === 'DOM') {
        this.showDomNotice(payload);
      } else if (isVisible) {
        this.showDomNotice(payload);
      } else {
        this.showSystemNotification(payload, true);
      }
      this.speak(payload);
      if (!isVisible) this.startTitleBlink(payload);
    },
    showDomNotice(payload) {
      this.activeNotice = payload;
      this.visible = true;
    },
    showSystemNotification(payload, fallbackToDom) {
      if (typeof Notification === 'undefined') {
        if (fallbackToDom) this.showDomNotice(payload);
        return;
      }
      const show = () => {
        try {
          const notice = new Notification(payload.title || '百度营销推广', {
            body: payload.content || '百度营销推广有新数据，请立即处理！',
            tag: payload.id || payload.leadRawId || 'BAIDU_PUSH',
            renotify: true,
            requireInteraction: true
          });
          notice.onclick = () => {
            window.focus();
            notice.close();
            this.activeNotice = payload;
            this.goProcess();
          };
        } catch (e) {
          if (fallbackToDom) this.showDomNotice(payload);
        }
      };
      if (Notification.permission === 'granted') {
        show();
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') show();
          else if (fallbackToDom) this.showDomNotice(payload);
        }).catch(() => {
          if (fallbackToDom) this.showDomNotice(payload);
        });
      } else if (fallbackToDom) {
        this.showDomNotice(payload);
      }
    },
    speak(payload) {
      const tpl = payload && payload.template ? payload.template : {};
      if (tpl.voiceEnabled === false) return;
      if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) return;
      try {
        this.stopSpeech();
        const utterance = new SpeechSynthesisUtterance(payload.content || '');
        utterance.lang = 'zh-CN';
        utterance.rate = 1;
        window.speechSynthesis.speak(utterance);
      } catch (e) {
        // ignore browser speech restrictions
      }
    },
    stopSpeech() {
      try {
        if (window.speechSynthesis) window.speechSynthesis.cancel();
      } catch (e) {
        // ignore
      }
    },
    startTitleBlink(payload) {
      if (this.titleTimer) return;
      const text = payload && payload.title ? payload.title : '百度营销推广';
      this.titleTimer = setInterval(() => {
        document.title = document.title === this.originalTitle ? `【新消息】${text}` : this.originalTitle;
      }, 900);
    },
    stopTitleBlink() {
      clearInterval(this.titleTimer);
      this.titleTimer = null;
      if (this.originalTitle) document.title = this.originalTitle;
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.stopTitleBlink();
      }
    },
    goProcess() {
      const token = getToken();
      const phone = this.activeNotice && this.activeNotice.phoneCandidate ? String(this.activeNotice.phoneCandidate) : '';
      this.visible = false;
      this.stopTitleBlink();
      if (!token) {
        window.location.href = buildManageLoginEntryUrl(`/baidu-aifanfan?tab=review&keyword=${encodeURIComponent(phone)}`);
        return;
      }
      this.$router.push({
        path: '/baidu-aifanfan',
        query: {
          tab: 'review',
          keyword: phone
        }
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.baidu-lead-notice {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 3000;
  width: 360px;
  min-height: 156px;
  padding: 18px 18px 16px;
  border: 1px solid rgba(220, 223, 230, 0.9);
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(20, 28, 45, 0.18);
  line-height: 1.55;
}

.baidu-lead-notice--blink .baidu-lead-notice__content {
  animation: baiduLeadNoticeBlink 1s linear infinite;
}

.baidu-lead-notice__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.baidu-lead-notice__content {
  margin-top: 12px;
  word-break: break-word;
}

.baidu-lead-notice__meta {
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.78;
}

.baidu-lead-notice__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.baidu-lead-notice-enter-active,
.baidu-lead-notice-leave-active {
  transition: all 0.2s ease;
}

.baidu-lead-notice-enter,
.baidu-lead-notice-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@keyframes baiduLeadNoticeBlink {
  0%,
  52% {
    opacity: 1;
  }
  53%,
  100% {
    opacity: 0.35;
  }
}

@media (max-width: 640px) {
  .baidu-lead-notice {
    right: 12px;
    bottom: 12px;
    width: calc(100vw - 24px);
  }
}
</style>
